import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Database, Copy, Check, ChevronDown, ChevronRight, Loader2, Trash2 } from 'lucide-react';
import { triggerExport, retryExport, listExports, deleteExportFiles, getProjects, ExportJob } from '../../services/api';

type StatusFilter = 'submitted' | 'approved';
type Preset = 'whisper' | 'survey';

interface ProjectOpt { projectId: string; name: string; surveyProject?: boolean }

function fmtBytes(n: number | null): string {
  if (!n) return '—';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

function fmtHours(secs: number | null): string {
  if (!secs) return '—';
  return `${(secs / 3600).toFixed(2)} h`;
}

function CopyButton({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); }}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
      title="Copy to clipboard"
    >
      {done ? <Check size={12} /> : <Copy size={12} />}
      {done ? 'Copied' : 'Copy'}
    </button>
  );
}

const STATUS_COLORS: Record<string, string> = {
  queued: 'bg-gray-100 text-gray-700',
  running: 'bg-blue-100 text-blue-700',
  completed: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-red-100 text-red-700',
};

function CompletedActions({ job }: { job: ExportJob }) {
  const [path, setPath] = useState(`~/datasets/dataone-${job.jobId.slice(0, 8)}`);
  const [showHelp, setShowHelp] = useState(false);
  const cmd = `aws s3 sync "${job.s3Uri}" "${path}" --profile company`;
  return (
    <div className="mt-2 space-y-2">
      <div className="flex items-center gap-2">
        <label className="text-xs text-gray-500 shrink-0">Download to</label>
        <input
          className="input flex-1 !py-1 text-sm"
          value={path}
          onChange={(e) => setPath(e.target.value)}
          spellCheck={false}
        />
      </div>
      <div className="flex items-start gap-2">
        <code className="flex-1 block bg-gray-900 text-gray-100 rounded-md px-3 py-2 text-xs overflow-x-auto whitespace-pre">{cmd}</code>
        <CopyButton text={cmd} />
      </div>
      <button
        onClick={() => setShowHelp((s) => !s)}
        className="inline-flex items-center gap-1 text-xs text-violet-700 hover:text-violet-900"
      >
        {showHelp ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        How to download
      </button>
      {showHelp && (
        <ol className="text-xs text-gray-600 list-decimal ml-5 space-y-1">
          <li>Install the AWS CLI — <code>brew install awscli</code> (macOS), the MSI installer (Windows), or <code>pip install awscli</code>.</li>
          <li>Configure credentials once — <code>aws configure --profile company</code> (or reuse the profile you deploy with).</li>
          <li>Run the command above. The dataset (<code>clips/</code>, <code>train/val/test.jsonl</code>, <code>metadata.csv</code>, <code>checksums.sha256</code>) downloads to your chosen folder.</li>
          <li>If the connection drops, <b>re-run the same command</b> — it resumes and skips finished files.</li>
          <li>Verify integrity — <code>cd {path} && shasum -a 256 -c checksums.sha256</code></li>
        </ol>
      )}
    </div>
  );
}

// Must match STALE_CLAIM_MINUTES in whisper-worker/worker/export_job.py — the
// worker only lets another box take over a 'running' job once the heartbeat is
// this old, so offering Retry any earlier would just be rejected.
const STALE_AFTER_MS = 30 * 60 * 1000;

const isStale = (heartbeatAt: string | null): boolean =>
  heartbeatAt != null && Date.now() - new Date(heartbeatAt).getTime() > STALE_AFTER_MS;

const staleFor = (heartbeatAt: string | null): string => {
  if (!heartbeatAt) return 'a while';
  const mins = Math.floor((Date.now() - new Date(heartbeatAt).getTime()) / 60000);
  return mins >= 120 ? `${Math.floor(mins / 60)}h` : `${mins}m`;
};

export default function DataExportPage() {
  const qc = useQueryClient();
  const [preset, setPreset] = useState<Preset>('whisper');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('approved');
  const [projectId, setProjectId] = useState('');
  const [includeVideos, setIncludeVideos] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const { data: jobs = [] } = useQuery({
    queryKey: ['exports'],
    queryFn: listExports,
    refetchInterval: 10_000,
  });

  // Survey export is scoped to a single project. Any project can be exported in
  // this per-video format — not just those flagged "Survey Project".
  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => getProjects({ status: 'all' }),
  });
  const exportableProjects: ProjectOpt[] = useMemo(
    () => ((projectsData?.projects ?? []) as ProjectOpt[])
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name)),
    [projectsData],
  );

  // Incremental default: prefill start with the last completed export's end for
  // the same status filter, so a new run grabs only new data.
  const lastEndForFilter = useMemo(() => {
    const j = jobs.find((x) => x.status === 'completed' && x.statusFilter === statusFilter && x.endDate);
    return j?.endDate ?? null;
  }, [jobs, statusFilter]);

  const mutation = useMutation({
    mutationFn: () => triggerExport(
      preset === 'survey'
        ? { preset: 'survey', statusFilter, projectId, includeVideos }
        : {
            statusFilter,
            preset: 'whisper',
            startDate: startDate ? new Date(startDate).toISOString() : undefined,
            endDate: endDate ? new Date(endDate + 'T23:59:59').toISOString() : undefined,
          },
    ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['exports'] });
      setToast({ type: 'success', message: 'Export queued. It will appear below and update as it runs.' });
      setTimeout(() => setToast(null), 5000);
    },
    onError: (e: Error) => { setToast({ type: 'error', message: e.message }); setTimeout(() => setToast(null), 6000); },
  });

  const retryMutation = useMutation({
    mutationFn: (jobId: string) => retryExport(jobId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['exports'] });
      setToast({ type: 'success', message: 'Export re-queued. It resumes under the same job id and S3 prefix.' });
      setTimeout(() => setToast(null), 5000);
    },
    onError: (e: Error) => { setToast({ type: 'error', message: e.message }); setTimeout(() => setToast(null), 6000); },
  });

  const deleteFilesMutation = useMutation({
    mutationFn: (jobId: string) => deleteExportFiles(jobId),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['exports'] });
      setToast({ type: 'success', message: `Deleted ${d.objectsDeleted} files from S3. History kept.` });
      setTimeout(() => setToast(null), 5000);
    },
    onError: (e: Error) => { setToast({ type: 'error', message: e.message }); setTimeout(() => setToast(null), 6000); },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
          <Database size={20} className="text-violet-700" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Training Data Export</h1>
          <p className="text-sm text-gray-500">
            <b>Whisper</b>: a 16 kHz clip dataset from Submitted/Approved data (date-ranged, incremental).
            {' '}<b>Survey Export</b>: per-video full video + audio + speaker-separated transcript JSON for a chosen Survey Project.
          </p>
        </div>
      </div>

      {/* New export form */}
      <div className="card p-4 space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Preset</label>
          <div className="flex gap-2">
            {([['whisper', 'Whisper'], ['survey', 'Survey Export']] as [Preset, string][]).map(([p, lbl]) => (
              <button
                key={p}
                onClick={() => setPreset(p)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                  preset === p ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          {preset === 'survey' ? (
            <>
            <div className="min-w-[16rem]">
              <label className="block text-xs font-medium text-gray-500 mb-1">Project</label>
              <select className="input" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                <option value="">Choose a project…</option>
                {exportableProjects.map((p) => (
                  <option key={p.projectId} value={p.projectId}>
                    {p.name}{p.surveyProject ? ' (Survey)' : ''}
                  </option>
                ))}
              </select>
              {exportableProjects.length === 0 && (
                <p className="mt-1 text-xs text-amber-600">No projects found.</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Include</label>
              <div className="flex gap-2">
                {([[true, 'Videos + data'], [false, 'Data only (no videos)']] as [boolean, string][]).map(([v, lbl]) => (
                  <button
                    key={String(v)}
                    onClick={() => setIncludeVideos(v)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                      includeVideos === v ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Data</label>
                <div className="flex gap-2">
                  {(['submitted', 'approved'] as StatusFilter[]).map((s) => (
                    <button
                      key={s}
                      onClick={() => setStatusFilter(s)}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize ${
                        statusFilter === s ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">From (optional)</label>
                <input type="date" className="input max-w-[10rem]" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">To (optional)</label>
                <input type="date" className="input max-w-[10rem]" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
            </>
          )}
          <button
            className="btn-primary"
            disabled={mutation.isPending || (preset === 'survey' && !projectId)}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Queuing…' : 'Start Export'}
          </button>
        </div>
        {preset === 'survey' && (
          <p className="text-xs text-gray-500">
            Exports every transcribed recording in the project — {includeVideos ? <>full <code>video.mp4</code> + </> : null}
            <code>audio.wav</code> + a per-video <code>transcript.json</code> (timestamps, GPS, metadata;
            Speaker 1 = enumerator, Speaker 2 = respondent), plus a combined <code>dataset.jsonl</code>
            {includeVideos ? '.' : '. Videos are excluded.'}
          </p>
        )}
        {preset === 'whisper' && lastEndForFilter && !startDate && (
          <button
            className="text-xs text-violet-700 hover:text-violet-900"
            onClick={() => setStartDate(lastEndForFilter.slice(0, 10))}
          >
            ↳ Only new data since last {statusFilter} export ({new Date(lastEndForFilter).toLocaleDateString()})
          </button>
        )}
      </div>

      {/* Jobs list */}
      <div className="card p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Exports</h2>
        </div>
        <ul className="divide-y divide-gray-100">
          {jobs.length === 0 ? (
            <li className="px-4 py-6 text-sm text-gray-400 text-center">No exports yet.</li>
          ) : (
            jobs.map((job) => (
              <li key={job.jobId} className="px-4 py-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[job.status] ?? 'bg-gray-100'}`}>
                      {(job.status === 'running' || job.status === 'queued') && <Loader2 size={11} className="animate-spin" />}
                      {job.status}
                    </span>
                    <span className="text-sm font-medium text-gray-900 capitalize">{job.statusFilter}</span>
                    <span className="text-xs text-gray-400">{job.preset}</span>
                    {(job.startDate || job.endDate) && (
                      <span className="text-xs text-gray-500">
                        {job.startDate ? new Date(job.startDate).toLocaleDateString() : '…'} → {job.endDate ? new Date(job.endDate).toLocaleDateString() : 'now'}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    {job.status === 'completed'
                      ? `${job.clipCount ?? 0} clips · ${fmtHours(job.totalSeconds)} · ${fmtBytes(job.totalBytes)}`
                      : job.status === 'running' && job.tasksTotal
                        ? `${job.tasksDone ?? 0}/${job.tasksTotal} tasks · ${job.clipCount ?? 0} clips`
                        : new Date(job.triggeredAt).toLocaleString()}
                  </div>
                </div>
                {job.status === 'running' && job.tasksTotal ? (
                  <div className="mt-2 h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-violet-500 transition-all duration-500"
                      style={{ width: `${Math.min(100, Math.round((100 * (job.tasksDone ?? 0)) / job.tasksTotal))}%` }}
                    />
                  </div>
                ) : null}
                {job.status === 'running' && isStale(job.heartbeatAt) && (
                  <p className="mt-1 text-xs text-amber-600">
                    No progress for {staleFor(job.heartbeatAt)} — the worker
                    {job.workerId ? ` (${job.workerId})` : ''} may have died. Use Retry to re-queue it.
                  </p>
                )}
                {job.status === 'running' && isStale(job.heartbeatAt) && (
                  <button
                    onClick={() => retryMutation.mutate(job.jobId)}
                    disabled={retryMutation.isPending}
                    className="mt-2 px-3 py-1 rounded-lg text-xs font-medium bg-amber-100 text-amber-700 hover:bg-amber-200 disabled:opacity-50"
                  >
                    Retry export
                  </button>
                )}
                {job.status === 'completed' && !job.filesDeletedAt && (
                  <>
                    <CompletedActions job={job} />
                    <button
                      onClick={() => {
                        if (window.confirm(
                          'Delete this export’s files from S3?\n\nThe job history (date, filters, counts) is kept, and the original source recordings are NOT affected — only the exported clips + manifests are removed.'
                        )) {
                          deleteFilesMutation.mutate(job.jobId);
                        }
                      }}
                      disabled={deleteFilesMutation.isPending}
                      className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
                    >
                      <Trash2 size={12} /> Delete files from S3
                    </button>
                  </>
                )}
                {job.filesDeletedAt && (
                  <p className="mt-2 text-xs text-gray-400 italic">
                    Files deleted from S3 on {new Date(job.filesDeletedAt).toLocaleString()} — history kept.
                  </p>
                )}
                {job.status === 'failed' && job.errorMessage && (
                  <p className="mt-1 text-xs text-red-600">{job.errorMessage}</p>
                )}
                {job.status === 'failed' && (
                  <button
                    onClick={() => retryMutation.mutate(job.jobId)}
                    disabled={retryMutation.isPending}
                    className="mt-2 px-3 py-1 rounded-lg text-xs font-medium bg-violet-100 text-violet-700 hover:bg-violet-200 disabled:opacity-50"
                  >
                    Retry export
                  </button>
                )}
              </li>
            ))
          )}
        </ul>
      </div>

      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-2.5 rounded-xl shadow-lg text-sm font-medium max-w-md ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}
